/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Building2, Lock, Loader2, UserPlus, Users, Activity, AlertTriangle, Pill, FileSpreadsheet, FileText, MapPinned } from "lucide-react";
import { WorryMap } from "../../components/WorryMap";
import { ChallengeAdmin } from "../../components/Challenge";

const Kpi = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={testid} className="bg-white rounded-2xl border border-[#E4E0D5] p-4 flex items-center gap-3">
    <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ backgroundColor: `${accent}1a`, color: accent }}><Icon size={22} /></div>
    <div><div className="font-[Fraunces] text-2xl font-bold text-[#0A1426] leading-none">{value}</div><div className="text-[11px] text-[#0A1426]/60 mt-1">{label}</div></div>
  </div>
);

export default function ClinicPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [locked, setLocked] = useState(false);
  const [nom, setNom] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("vue");

  const load = () => api.get("/clinic/dashboard").then(({ data }) => { setData(data); setLocked(false); })
    .catch((e) => { if (e.response?.status === 402) setLocked(true); else toast.error("Chargement impossible"); setData({ clinic: null, praticiens: [] }); });
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try { await api.post("/clinic", { nom }); toast.success("Clinique créée"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const invite = async () => {
    setBusy(true);
    try { const { data: r } = await api.post("/clinic/invite", { email }); toast.success(`${r.praticien.nom} ajouté(e)`); setEmail(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  const [mois, setMois] = useState(new Date().toISOString().slice(0, 7));
  const exportFile = async (ext) => {
    try {
      const { data: blob } = await api.get(`/clinic/export.${ext}?mois=${mois}`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `bombacare-clinique-${mois}.${ext}`; a.click();
      toast.success(`Export ${ext.toUpperCase()} téléchargé`);
    } catch (e) { toast.error("Export impossible"); }
  };

  if (!data) return <div className="py-20 grid place-items-center"><Loader2 className="animate-spin text-[#2BA3A0]" /></div>;

  if (locked) {
    return (
      <div className="bc-in flex flex-col gap-5" data-testid="pro-clinic">
        <h1 className="font-[Fraunces] text-3xl font-bold text-[#0A1426]">Vue Clinique</h1>
        <div data-testid="clinic-locked" className="rounded-3xl bg-[#0A1426] text-white p-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <Lock size={32} className="text-[#D4AF5A]" />
          <div className="flex-1">
            <div className="font-[Fraunces] text-xl font-bold">Réservée au forfait Clinique</div>
            <p className="text-[13px] text-white/80">Vue multi-praticiens, statistiques globales et comparaison des taux d'assiduité entre praticiens.</p>
          </div>
          <Button data-testid="clinic-upgrade-btn" onClick={() => navigate("/pro/subscription")} className="h-11 rounded-full bg-[#D4AF5A] text-[#0A1426] font-bold">Passer au forfait Clinique</Button>
        </div>
      </div>
    );
  }

  const g = data.global;
  const maxAss = Math.max(1, ...data.praticiens.map((p) => p.assiduite_pct));

  return (
    <div className="bc-in flex flex-col gap-5" data-testid="pro-clinic">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold">Forfait Clinique</div>
          <h1 className="font-[Fraunces] text-3xl font-bold text-[#0A1426]">{data.clinic ? data.clinic.nom : "Vue Clinique"}</h1>
        </div>
        {data.clinic && (
          <div data-testid="clinic-export" className="flex items-center gap-2 bg-white border border-[#E4E0D5] rounded-full px-2 py-1">
            <input data-testid="clinic-export-month" type="month" value={mois} onChange={(e) => setMois(e.target.value)} className="h-9 px-2 text-sm text-[#0A1426] bg-transparent focus:outline-none" />
            <button data-testid="clinic-export-csv" onClick={() => exportFile("csv")} className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-[#F5F3EE] text-[#0A1426] text-[12px] font-bold"><FileSpreadsheet size={14} /> CSV</button>
            <button data-testid="clinic-export-pdf" onClick={() => exportFile("pdf")} className="inline-flex items-center gap-1 h-9 px-3 rounded-full bg-[#0A1426] text-white text-[12px] font-bold"><FileText size={14} /> PDF</button>
          </div>
        )}
      </div>

      {!data.clinic ? (
        <div className="bg-white rounded-3xl border border-dashed border-[#E4E0D5] p-5 flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="flex items-center gap-2 text-[#0A1426] font-bold text-sm"><Building2 size={18} /> Créer ma clinique</div>
          <input data-testid="clinic-name-input" value={nom} onChange={(e) => setNom(e.target.value)} placeholder="Nom de la clinique" className="flex-1 h-11 px-4 rounded-full border border-[#E4E0D5] text-sm text-[#0A1426]" />
          <Button data-testid="clinic-create-btn" disabled={busy || nom.length < 2} onClick={create} className="h-11 rounded-full bg-[#2BA3A0] text-white font-bold">Créer</Button>
        </div>
      ) : (
        <>
          <div className="flex gap-2" data-testid="clinic-tabs">
            {[["vue", "Vue d'ensemble", Activity], ["carte", "Carte des signalements", MapPinned]].map(([k, l, I]) => (
              <button key={k} data-testid={`clinic-tab-${k}`} onClick={() => setTab(k)} className={`h-10 px-4 rounded-full text-[13px] font-bold inline-flex items-center gap-2 ${tab === k ? "bg-[#0A1426] text-white" : "bg-white border border-[#E4E0D5] text-[#0A1426]"}`}><I size={15} /> {l}</button>
            ))}
          </div>
          {tab === "carte" && (
            <>
              <WorryMap clinic />
              <p className="text-[11px] text-[#0A1426]/60">Une couleur par praticien · touchez un nom dans la légende pour le masquer / l'afficher · marqueurs estompés = signalements traités.</p>
            </>
          )}
          {tab === "vue" && (<>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Kpi testid="clinic-kpi-praticiens" icon={Users} label="Praticiens" value={g.praticiens} accent="#0A1426" />
            <Kpi testid="clinic-kpi-patients" icon={Users} label="Patients suivis" value={g.patients} accent="#2BA3A0" />
            <Kpi testid="clinic-kpi-assiduite" icon={Activity} label="Assiduité (7 j)" value={`${g.assiduite_pct}%`} accent="#3a8acb" />
            <Kpi testid="clinic-kpi-alertes" icon={AlertTriangle} label="Alertes ouvertes" value={g.alertes} accent="#D9534F" />
            <Kpi testid="clinic-kpi-observance" icon={Pill} label="Observance moy." value={g.observance_pct == null ? "—" : `${g.observance_pct}%`} accent="#D4AF5A" />
          </div>

          <div className="bg-white rounded-3xl border border-[#E4E0D5] p-5">
            <h2 className="text-[11px] uppercase tracking-wider text-[#0A1426]/60 font-bold mb-3">Comparaison de l'assiduité par praticien</h2>
            <div className="flex flex-col gap-3">
              {data.praticiens.map((p, i) => (
                <div key={p.praticien_id} data-testid={`clinic-prat-${p.praticien_id}`} className="grid grid-cols-[28px_1fr] gap-3 items-center">
                  <span className="w-7 h-7 rounded-full bg-[#F5F3EE] text-[#0A1426] text-[12px] font-bold grid place-items-center">{i + 1}</span>
                  <div>
                    <div className="flex justify-between text-[13px] text-[#0A1426]">
                      <span className="font-bold">{p.nom}</span>
                      <span>{p.assiduite_pct}% · {p.patients} patients · {p.seances_semaine} séances · {p.alertes} alertes · obs. {p.observance_pct == null ? "—" : `${p.observance_pct}%`}</span>
                    </div>
                    <div className="h-3 bg-[#F5F3EE] rounded-full overflow-hidden mt-1">
                      <div className="h-full rounded-full transition-all" style={{ width: `${Math.max(3, p.assiduite_pct / maxAss * 100)}%`, backgroundColor: p.assiduite_pct >= 60 ? "#2BA3A0" : p.assiduite_pct >= 30 ? "#D4AF5A" : "#D9534F" }} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <ChallengeAdmin />

          {data.est_admin && (
            <div className="bg-white rounded-2xl border border-dashed border-[#E4E0D5] p-4 flex flex-col sm:flex-row gap-2 sm:items-center">
              <div className="flex items-center gap-2 text-[#0A1426] font-bold text-sm"><UserPlus size={18} /> Ajouter un praticien</div>
              <input data-testid="clinic-invite-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="e-mail du praticien" className="flex-1 h-11 px-4 rounded-full border border-[#E4E0D5] text-sm text-[#0A1426]" />
              <Button data-testid="clinic-invite-btn" disabled={busy || !email} onClick={invite} className="h-11 rounded-full bg-[#0A1426] text-white font-bold">Ajouter</Button>
            </div>
          )}
          </>)}
        </>
      )}
    </div>
  );
}
